import { SearchOutlined } from '@ant-design/icons';
import { Breadcrumb, Button, Col, Input, message, Pagination, Popconfirm, Row, Space, Table, Tooltip } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ReactComponent as ArchiveRestore } from '../../../Images/ArchiveRestore.svg';
import { ReactComponent as Back } from '../../../Images/Back.svg';
import { ReactComponent as FilterIcon } from '../../../Images/FilterIcon.svg';
import { useDispatch, useSelector } from 'react-redux';
import { fetchArchivedSalesOrderList, unArchiveSalesOrder } from '../../../slices/sales-order';
import dayjs from 'dayjs';

function ArchiveSalesOrderList() {

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pagesize, setPageSize] = useState(20);
  const [orderBy, setOrderBy] = useState('');
  const dispatch = useDispatch();
  const { data = [], totalRecord } = useSelector(state => state.salesOrder);
  const handlePageChange = (newPage) => {
    setPage(newPage);
    setOrderBy('')
  };
  

  const handleRestore = (id) => {
    if (!id) {
      message.error('Missing ID for deletion!');
      return;
    }

    dispatch(unArchiveSalesOrder(id))
      .unwrap()
      .then(() => {
        message.success('Sales Order Unarchived Successfully!');
        dispatch(
          fetchArchivedSalesOrderList({ page, pagesize, ...(search && { search: search }) }),
        );
      })
      .catch((err) => {
        console.error('Delete Error:', err);
        message.error(`Error: ${err.message || 'Failed to delete the profile.'}`);
      });
  };
  const columns = [
    { title: 'Sr. No.', dataIndex: 'SrNo', key: 'SrNo', sorter: true, width: 100,  render: (text, record, index) => {
        return (page - 1) * pagesize + index + 1;
      }},
     {
      title: 'Order No.',
      dataIndex: 'sales_order_no',
      key: 'sales_order_no',
      sorter: true,
    },
    {
      title: 'Order Date',
      dataIndex: 'order_date',
      key: 'order_date',
      sorter: true,
    },
    
    {
      title: 'PO No.',
      dataIndex: 'purchase_order_number',
      key: 'purchase_order_number',
      sorter: true,
    },
    {
      title: 'PO Date',
      dataIndex: 'purchase_order_date',
      key: 'purchase_order_date',
    },
    {
      title: 'Customer Name',
      dataIndex: 'customer_name',
      key: 'customer_name',
      sorter: true,
      render: (customer_name) => {
        return (
          <span>
            {customer_name || 'N/A'}
          </span>
        )
      },
      
    },

    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      sorter: true,
    },
    {
      title: 'Archive At',
      dataIndex: 'updated_at',
      key: 'updated_at',
      sorter: true,
      render: (record) => {
        return (
          <span>{record ? dayjs(record).format('DD-MM-YYYY HH:mm') : 'N/A'} </span>
        )
      }
    },
    {
      title: 'Archive By',
      dataIndex: 'updated_by',
      key: 'updated_by',
      sorter: true,
      render: (record) => {
        return (
          <span>{record ? `${record.first_name} ` : 'N/A'} </span>
        )
      }

    },
    {
      title: 'Action',
      dataIndex: 'action',
      fixed: 'right',
      width: 100,
      render: (_, record) => (
        <Space>
          <Tooltip placement="top" title="Restore">
            <Popconfirm
              title="Are you sure you want to Unarchive this record?"
              onConfirm={() => handleRestore(record.id)} // Trigger delete on confirmation
              onCancel={() => message.info('Action cancelled')} // Optional cancel action
              okText="Yes"
              cancelText="No"
            >
              <ArchiveRestore className="ArchiverestorImg" />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  
  useEffect(() => {
    dispatch(
      fetchArchivedSalesOrderList({
        page,
        pagesize,
        ...(search && { search: search }),
        ...(orderBy && { ordering: orderBy }),
      }),
    );
  }, [dispatch, page, pagesize, search, orderBy]);

  const debounceTimeout = useRef(null);
  const handleSearch = (e) => {
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(() => {
      setSearch(e.target.value);
      setPage(1);
    }, 100); // 300ms delay
  };
  const resetFilter = () => {
    setSearch('');
    setOrderBy('');
    setPage(1);
  };

  const GetSortingEnumValue = (sortField, sortOrder) => {
    let sortingEnumKey = 'id';
    if (sortField === 'SrNo' && sortOrder === 'ascend') {
      sortingEnumKey = 'SrNo';
    }
    if (sortField === 'SrNo' && sortOrder === 'descend') {
      sortingEnumKey = '-SrNo';
    }
    if (sortField === 'order_date' && sortOrder === 'ascend') {
      sortingEnumKey = 'order_date';
    }
    if (sortField === 'order_date' && sortOrder === 'descend') {
      sortingEnumKey = '-order_date';
    }
    if (sortField === 'sales_order_no' && sortOrder === 'ascend') {
      sortingEnumKey = 'sales_order_no';
    }
    if (sortField === 'sales_order_no' && sortOrder === 'descend') {
      sortingEnumKey = '-sales_order_no';
    }
    if (sortField === 'purchase_order_number' && sortOrder === 'ascend') {
      sortingEnumKey = 'purchase_order_number';
    }
    if (sortField === 'purchase_order_number' && sortOrder === 'descend') {
      sortingEnumKey = '-purchase_order_number';
    }
    if (sortField === 'customer_name' && sortOrder === 'ascend') {
      sortingEnumKey = 'customer_name';
    }
    if (sortField === 'customer_name' && sortOrder === 'descend') {
      sortingEnumKey = '-customer_name';
    }
    if (sortField === 'status' && sortOrder === 'ascend') {
      sortingEnumKey = 'status';
    }
    if (sortField === 'status' && sortOrder === 'descend') {
      sortingEnumKey = '-status';
    }
    if (sortField === 'created_by' && sortOrder === 'ascend') {
      sortingEnumKey = 'created_by__first_name';
    }
    if (sortField === 'created_by' && sortOrder === 'descend') {
      sortingEnumKey = '-created_by__first_name';
    }
    if (sortField === 'created_by__last_name' && sortOrder === 'ascend') {
      sortingEnumKey = 'created_by__last_name';
    }
    if (sortField === 'created_by__last_name' && sortOrder === 'descend') {
      sortingEnumKey = '-created_by__last_name';
    }
    if (sortField === 'updated_by' && sortOrder === 'ascend') {
      sortingEnumKey = 'updated_by__first_name';
    }
    if (sortField === 'updated_by' && sortOrder === 'descend') {
      sortingEnumKey = '-updated_by__first_name';
    }
    if (sortField === 'updated_by__last_name' && sortOrder === 'ascend') {
      sortingEnumKey = 'updated_by__last_name';
    }
    if (sortField === 'updated_by__last_name' && sortOrder === 'descend') {
      sortingEnumKey = '-updated_by__last_name';
    }
    return sortingEnumKey;
  };

  const handleTableChange = async (pagination, filters, sorter, ordering) => {
    if (sorter?.columnKey && sorter.order) {
      const orderBy = GetSortingEnumValue(sorter.columnKey, sorter.order);
      setOrderBy(orderBy);
    }
  };
  
  const handlePageSizeChange = (current, size) => {
    setPage(1); // Reset to first page if needed
    setPageSize(size); // You must have a setter or state update
  };

  return (
    <>
      <div className="mainwrap">
        <div className='sticyaction'>
          <Row className="pagenamerow" justify="space-between" align="middle">
            <Col>
              <h2>Archive Sales Orders</h2>
              <div className="bredcrumbwrp">
                <Link to="/sales-order-list" className="back">
                  <Back /> BACK
                </Link>
                <Breadcrumb
                  items={[
                    { title: <Link to="/dashboard">Home</Link> },
                    { title: <Link to="/sales-order-list">Sales Orders</Link> },
                    { title: 'Archive Sales Orders' }
                  ]}
                />
              </div>
            </Col>
            <Col>
              <Space size="middle">
                <Input
                  placeholder="Search"
                  className='searchbox'
                  prefix={<SearchOutlined />}
                  value={search}
                  onChange={handleSearch}
                  style={{ width: '100%' }}
                />
                <Link >
                  <Tooltip title="Filter">
                    <Button type="primary" disabled={!search && !orderBy} className='iconlink' onClick={resetFilter}>
                      <FilterIcon />
                    </Button>
                  </Tooltip>
                </Link>
              </Space>
            </Col>

          </Row>
        </div>
        <div className="tablewrp">
          <Table
            dataSource={data}
            columns={columns}
            pagination={false}
            onChange={handleTableChange}
          />
        </div>
        <Pagination
          current={page}
          pageSize={pagesize}
          total={totalRecord}
          showSizeChanger
          pageSizeOptions={['20', '50', '100']}
          onShowSizeChange={handlePageSizeChange}
          showTotal={total => `Total ${total} items`}
          onChange={handlePageChange}
          locale={{ items_per_page: 'Items / Page' }}
        />
      </div >
    </>
  );
}

export default ArchiveSalesOrderList;
